/**
 * SessionProvider — resolve sessão e identidade.
 *
 * Dois modos:
 *
 * 1. Modo URL (rota standalone /mesa/:tableId/module_lab/:sessionId):
 *    lê params + query e gera anonymous userId se necessário.
 *
 * 2. Modo embed (prop `embedSession`): usa diretamente a Session passada.
 *    Usado quando LabRuntime é montado dentro da GameSession (overlay
 *    full-screen do LabSessionViewer), com identidade já resolvida via JWT
 *    da mesa.
 *
 * Query params (modo URL):
 *   ?role=master|player   (default: player)
 *   ?userId=...           (default: UUID persistido em localStorage)
 *   ?displayName=...      (opcional)
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import type { Role, Session } from '../types/session'

const USER_ID_KEY = 'module_lab:userId'

/** Context exposto para permitir providers alternativos (ex.: LabPreview
 *  injeta session synthetic em vez de ler da URL). */
export const SessionContext = createContext<Session | null>(null)

function getOrCreateAnonymousUserId(): string {
  const existing = localStorage.getItem(USER_ID_KEY)
  if (existing) return existing
  const generated =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? `anon-${crypto.randomUUID()}`
      : `anon-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`
  localStorage.setItem(USER_ID_KEY, generated)
  return generated
}

interface Props {
  children: ReactNode
  /** Quando passado, ignora URL params e usa esta sessão direto. */
  embedSession?: Session
}

export function SessionProvider({ children, embedSession }: Props) {
  if (embedSession) {
    return <SessionContext.Provider value={embedSession}>{children}</SessionContext.Provider>
  }
  return <UrlSessionProvider>{children}</UrlSessionProvider>
}

function UrlSessionProvider({ children }: { children: ReactNode }) {
  const params = useParams<{ tableId: string; sessionId: string }>()
  const [search] = useSearchParams()

  const session = useMemo<Session | null>(() => {
    const tableId = params.tableId
    const sessionId = params.sessionId
    if (!tableId || !sessionId) return null

    const roleParam = search.get('role')
    const role: Role = roleParam === 'master' ? 'master' : 'player'

    const urlUserId = search.get('userId')
    const userId = urlUserId ?? getOrCreateAnonymousUserId()
    const isAnonymous = userId.startsWith('anon-')

    const displayName = search.get('displayName') ?? undefined

    return {
      sessionId,
      userId,
      isAnonymous,
      role,
      displayName,
      tableId,
    }
  }, [params.tableId, params.sessionId, search])

  if (!session) {
    return (
      <div style={{ padding: '2rem', color: '#D4474A', fontFamily: 'monospace' }}>
        Parâmetros de rota inválidos: faltando tableId ou sessionId.
      </div>
    )
  }

  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>
}

export function useSession(): Session {
  const s = useContext(SessionContext)
  if (!s) {
    throw new Error('useSession deve ser usado dentro de <SessionProvider>')
  }
  return s
}
